import { Link } from "@tanstack/react-router";
import { Instagram, Facebook } from "lucide-react";
import logoAsset from "@/assets/optimized/logo-64.webp.asset.json";
import { SHOP_ENABLED } from "@/lib/feature-flags";

const SOCIALS = [
  {
    name: "Instagram",
    href: "https://www.instagram.com/smartasaurusofficial",
    icon: Instagram,
  },
  {
    name: "TikTok",
    href: "https://www.tiktok.com/@smartasaurusofficial",
    icon: TikTokIcon,
  },
  {
    name: "Facebook",
    href: "https://www.facebook.com/share/1DRXUg7iRN/",
    icon: Facebook,
  },
  {
    name: "Threads",
    href: "https://www.threads.com/@smartasaurusofficial",
    icon: ThreadsIcon,
  },
] as const;

function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M19.6 6.3a5.3 5.3 0 01-3.1-1 5.3 5.3 0 01-2.1-3.5h-3.3v13.2a2.7 2.7 0 11-2.7-2.7c.3 0 .5 0 .8.1V8.9a6 6 0 00-.8-.1 6 6 0 106 6V8.7a8.5 8.5 0 005.2 1.8V7.2a5.3 5.3 0 01-2-.9z" />
    </svg>
  );
}

function ThreadsIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M12.2 2C6.6 2 2.5 5.7 2.5 12s4.1 10 9.7 10c4.3 0 7-1.9 8.6-5.1l-2.5-1c-1 2.1-2.7 3.3-6.1 3.3-3.8 0-6.5-2.4-6.9-6.4h15.6c.1-.5.1-1.1.1-1.7C20.9 5.7 17.4 2 12.2 2zm-.1 2.7c3.4 0 5.7 1.8 6.3 5H5.5c.6-3.1 3-5 6.6-5z" />
    </svg>
  );
}

/**
 * Compact footer - minimal links plus official social profiles.
 */
export function SiteFooter() {
  return (
    <footer className="border-t border-border bg-card/30">
      <div className="mx-auto flex max-w-7xl flex-col items-center gap-4 px-4 py-6 text-xs text-muted-foreground md:px-8">
        <div className="flex w-full flex-col items-center justify-between gap-4 md:flex-row">
          <Link to="/" className="flex flex-col items-center gap-1 text-foreground md:items-start">
            <span className="flex items-center gap-2">
              <img
                src={logoAsset.url}
                alt=""
                width={20}
                height={20}
                loading="lazy"
                className="h-5 w-5"
              />
              <span className="notranslate font-display text-sm tracking-wide" translate="no">
                <span aria-hidden>🦖 </span>SMARTASAURUS<span aria-hidden> 🧠</span>
              </span>
            </span>
            <span className="text-[10px] font-semibold uppercase tracking-[0.28em] text-primary/90">
              Curiosity looks good on you.
            </span>
          </Link>

          <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
            <Link
              to="/discoveries"
              className="inline-flex min-h-11 items-center px-2 hover:text-foreground"
            >
              Discover
            </Link>
            <Link
              to="/collections"
              className="inline-flex min-h-11 items-center px-2 hover:text-foreground"
            >
              Series
            </Link>
            <Link
              to="/play"
              className="inline-flex min-h-11 items-center px-2 hover:text-foreground"
            >
              Play
            </Link>
            {SHOP_ENABLED && (
              <Link
                to="/shop"
                className="inline-flex min-h-11 items-center px-2 hover:text-foreground"
              >
                Shop
              </Link>
            )}
            <Link
              to="/survival"
              className="inline-flex min-h-11 items-center px-2 hover:text-foreground"
            >
              Survival protocols
            </Link>
            <Link
              to="/about"
              className="inline-flex min-h-11 items-center px-2 hover:text-foreground"
            >
              About
            </Link>
            <Link
              to="/contact"
              className="inline-flex min-h-11 items-center px-2 hover:text-foreground"
            >
              Contact
            </Link>
            <Link
              to="/contact"
              search={{ topic: "feedback" } as any}
              aria-label="Send feedback"
              className="inline-flex min-h-11 items-center px-2 hover:text-foreground"
            >
              Feedback
            </Link>
            <Link
              to="/release-notes"
              className="inline-flex min-h-11 items-center px-2 hover:text-foreground"
            >
              Release notes
            </Link>
          </div>
          <div className="flex items-center gap-2">
            {SOCIALS.map(({ name, href, icon: Icon }) => (
              <a
                key={name}
                href={href}
                target="_blank"
                rel="noopener noreferrer me"
                aria-label={`Smartasaurus on ${name}`}
                title={name}
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-border bg-card text-muted-foreground transition hover:border-primary hover:text-primary"
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>
        <div className="flex w-full flex-col items-center justify-between gap-2 border-t border-border/50 pt-3 text-xs md:flex-row">
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
            <Link to="/privacy" className="inline-flex min-h-11 items-center hover:text-foreground">
              Privacy
            </Link>
            <span aria-hidden>·</span>
            <Link to="/terms" className="inline-flex min-h-11 items-center hover:text-foreground">
              Terms
            </Link>
            <span aria-hidden>·</span>
            <Link to="/refunds" className="inline-flex min-h-11 items-center hover:text-foreground">
              Refunds
            </Link>
          </div>
          <div>
            © {new Date().getFullYear()}{" "}
            <span className="notranslate" translate="no">
              Smartasaurus
            </span>
            ™ · All rights reserved · Made in Sweden
          </div>
        </div>
      </div>
    </footer>
  );
}
